import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import CountUp from 'react-countup';
import { 
  Shield, 
  Radar, 
  MapPin, 
  Zap, 
  CheckCircle, 
  ArrowRight,
  Mail,
  AlertTriangle,
  Star,
  Play,
  Radio,
  FileText,
  Siren
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_BG = "https://customer-assets.emergentagent.com/job_f7db9eb5-303b-40e1-ba38-b76b077cd7ba/artifacts/pq6j6m1d_Gemini_Generated_Image_mazqtmazqtmazqtm.png";

const stats = [
  { value: 50000, label: 'Projects Completed', suffix: '+' },
  { value: 15, label: 'Years Experience', suffix: '+' },
  { value: 24, label: 'Hour Response', suffix: 'h' },
  { value: 99, label: 'Client Satisfaction', suffix: '%' },
];

const services = [
  {
    icon: Radio,
    title: 'Public Utility Locating',
    description: 'Professional marking of public utility lines before excavation.',
    link: '/services#public'
  },
  {
    icon: Radar,
    title: 'Private Utility Locating',
    description: 'Locate private utilities not covered by public services.',
    link: '/services#private'
  },
  {
    icon: Shield,
    title: 'Ground-Penetrating Radar',
    description: 'Advanced GPR technology for subsurface detection.',
    link: '/services#gpr'
  },
  {
    icon: MapPin,
    title: 'Utility Mapping & GIS',
    description: 'Detailed mapping and geographic information systems.',
    link: '/services#mapping'
  },
  {
    icon: FileText,
    title: 'Damage Prevention',
    description: 'Consulting services to prevent utility strikes.',
    link: '/services#damage'
  },
  {
    icon: Siren,
    title: 'Emergency Locates',
    description: '24/7 emergency utility locating services.',
    link: '/services#emergency'
  },
];

const testimonials = [
  {
    quote: "USG's team responded within hours and their GPR scanning prevented a major water main break. Professional and thorough.",
    author: "Michael Chen",
    role: "Construction Manager, BuildRight Inc.",
    rating: 5
  },
  {
    quote: "We've used USG for all our utility locating needs. Their accuracy and documentation is unmatched in the industry.",
    author: "Sarah Johnson",
    role: "Project Director, Metro Development",
    rating: 5
  },
  {
    quote: "The emergency response team saved us from a potential gas line strike. Their technology and expertise are top-notch.",
    author: "David Rodriguez",
    role: "Operations Manager, City Utilities",
    rating: 5
  },
  {
    quote: "Professional, accurate, and fast. USG has become our go-to utility locating partner for all municipal projects.",
    author: "Emily Thompson",
    role: "City Engineer, Springfield",
    rating: 5
  },
];

function AnimatedCounter({ value, suffix, label }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <div ref={ref} className="text-center">
      <div className="stat-number text-skytec-primary glitch-text">
        {isInView ? (
          <CountUp end={value} duration={2.5} separator="," />
        ) : '0'}
        <span className="text-skytec-secondary">{suffix}</span>
      </div>
      <div className="text-skytec-fg/60 font-mono text-sm uppercase tracking-wider mt-2">
        {label}
      </div>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, description, link, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      <Link 
        to={link}
        className="service-card block h-full p-6 bg-skytec-surface/50 border border-white/5 rounded-xl hover:border-skytec-primary/50 transition-all duration-500 group"
        data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="w-14 h-14 bg-skytec-primary-dim rounded-lg flex items-center justify-center mb-4 group-hover:bg-skytec-primary/20 transition-colors">
          <Icon className="w-7 h-7 text-skytec-primary" />
        </div>
        <h3 className="font-heading font-bold text-lg text-skytec-fg mb-2 group-hover:text-skytec-primary transition-colors">
          {title}
        </h3>
        <p className="text-skytec-fg/60 text-sm mb-4">
          {description}
        </p>
        <div className="flex items-center text-skytec-primary text-sm font-medium">
          Learn More 
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
        </div>
      </Link>
    </motion.div>
  );
}

export default function HomePage() {
  return (
    <div className="bg-skytec-bg">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG} 
            alt="Utility Locating" 
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 15%' }}
          />
          <div className="absolute inset-0 bg-skytec-bg/40" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 scan-grid opacity-20" />
        </div>

        {/* Scanning Beam */}
        <div className="scanning-beam" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-skytec-secondary-dim rounded-full mb-6">
              <AlertTriangle className="w-4 h-4 text-skytec-secondary mr-2" />
              <span className="text-skytec-secondary font-mono text-sm">CALL BEFORE YOU DIG</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-skytec-fg mb-6 tracking-tight uppercase">
              <span className="neon-text">PRECISION.</span>{' '}
              <span className="text-skytec-fg">PROTECTION.</span>{' '}
              <span className="text-skytec-secondary">PROGRESS.</span>
            </h1>
            
            <p className="text-skytec-fg/70 text-lg sm:text-xl max-w-3xl mx-auto mb-10 font-body">
              Advanced underground utility locating technology protecting infrastructure 
              and lives across the nation. Precision you can trust.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold text-lg px-8 py-6 btn-primary"
                  data-testid="hero-cta-locate"
                >
                  REQUEST A LOCATE
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-primary text-skytec-primary hover:bg-skytec-primary hover:text-white font-heading font-bold text-lg px-8 py-6"
                  data-testid="hero-cta-quote"
                >
                  GET A QUOTE
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-skytec-fg/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-skytec-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 border-y border-skytec-border bg-skytec-surface" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="relative">
                <AnimatedCounter {...stat} />
                {index < stats.length - 1 && (
                  <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-16 bg-skytec-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Services</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              PRECISION UTILITY LOCATING
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto">
              Comprehensive underground utility detection and mapping services 
              using state-of-the-art technology and certified professionals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} index={index} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <Button 
                variant="outline"
                className="border-skytec-primary text-skytec-primary hover:bg-skytec-primary hover:text-skytec-bg font-heading"
                data-testid="services-view-all"
              >
                VIEW ALL SERVICES
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Utility Locating Matters */}
      <section className="py-24 bg-skytec-surface relative overflow-hidden" data-testid="why-matters-section">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-skytec-secondary font-mono text-sm tracking-widest uppercase">Safety First</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
                WHY UTILITY LOCATING MATTERS
              </h2>
              <p className="text-skytec-fg/70 mb-8">
                Every year, thousands of underground utility strikes occur, leading to service 
                disruptions, costly repairs, injuries, and even fatalities. Professional utility 
                locating is your first line of defense against these preventable accidents.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Prevent costly damage to underground infrastructure',
                  'Protect workers and the public from dangerous utility strikes',
                  'Avoid service disruptions to communities',
                  'Ensure compliance with dig safety regulations',
                  'Save time and money on construction projects'
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-skytec-accent mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-skytec-fg/80">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/safety">
                <Button 
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading"
                  data-testid="learn-safety-btn"
                >
                  LEARN MORE ABOUT SAFETY
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden neon-border">
                <img 
                  src="https://images.unsplash.com/photo-1631583087046-13c813d34e90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBzYWZldHklMjBoZWxtZXQlMjB2ZXN0JTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTE1NDU4Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Safety Equipment"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-skytec-bg/80 to-transparent" />
                
                {/* Stats Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass-card rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-skytec-secondary font-mono text-2xl font-bold">400K+</div>
                        <div className="text-skytec-fg/60 text-sm">Utility strikes prevented annually by proper locating</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Testimonials</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              TRUSTED BY INDUSTRY LEADERS
            </h2>
          </motion.div>
        </div>

        {/* Testimonials Marquee */}
        <div className="relative">
          <div className="flex marquee">
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <div 
                key={index}
                className="flex-shrink-0 w-96 mx-4 p-6 bg-skytec-surface border border-skytec-border rounded-xl"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-skytec-secondary fill-skytec-secondary" />
                  ))}
                </div>
                <p className="text-skytec-fg/80 mb-6 text-sm leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div>
                  <div className="font-heading font-bold text-skytec-fg">{testimonial.author}</div>
                  <div className="text-skytec-fg/50 text-sm font-mono">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Banner */}
      <section className="relative overflow-hidden" data-testid="safety-banner">
        <div className="safety-stripe h-2" />
        <div className="bg-skytec-bg py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-skytec-surface border border-skytec-border rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-8"
            >
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-skytec-secondary-dim rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-10 h-10 text-skytec-secondary" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl lg:text-3xl font-bold text-skytec-fg uppercase">
                    CONTACT BEFORE YOU DIG
                  </h3>
                  <p className="text-skytec-fg/60 mt-2">
                    It's the law. It's free. It's smart. Contact us 
                    at least 2-3 business days before any excavation project.
                  </p>
                </div>
              </div>
              <Link to="/safety">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold whitespace-nowrap"
                  data-testid="safety-banner-cta"
                >
                  LEARN MORE
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
        <div className="safety-stripe h-2" />
      </section>

      {/* Final CTA Section */}
      <section className="py-24 relative" data-testid="final-cta-section">
        <div className="absolute inset-0 scan-grid opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-skytec-fg mb-6 uppercase tracking-tight">
              READY TO <span className="neon-text">PROTECT</span> YOUR PROJECT?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Get accurate utility locations before you break ground. Our team of certified 
              technicians is ready to help you dig safely.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-primary text-skytec-bg hover:bg-skytec-primary/90 font-heading font-bold text-lg px-10 py-6 btn-primary"
                  data-testid="final-cta-quote"
                >
                  GET YOUR FREE QUOTE
                </Button>
              </Link>
              <a href="mailto:info@usgllc811.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading font-bold text-lg px-10 py-6"
                  data-testid="final-cta-email"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  EMAIL US
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
